import React, { useEffect, useState } from "react";
import styled from "styled-components";
import sanityClient from "../sanityClient";
import LazyLoad from "react-lazyload";

const DynamicEvents = ({ category, categoryText }) => {
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [onTheHorizonEvents, setOnTheHorizonEvents] = useState([]);

  useEffect(() => {
    sanityClient
      .fetch(
        `*[_type == "event" && $category in categories] | order(startDate asc){
          _id,
          title,
          description,
          startDate,
          endDate,
          location,
          "imageUrl": image.asset->url,
          actionButtonText,
          actionButtonLink
        }`,
        { category }
      )
      .then((data) => {
        const now = new Date();
        const upcoming = [];
        const onTheHorizon = [];

        data.forEach((event) => {
          const eventStartDate = event.startDate
            ? new Date(event.startDate)
            : null;
          const eventEndDate = event.endDate ? new Date(event.endDate) : null;

          if (!eventStartDate) {
            onTheHorizon.push(event);
          } else if (eventEndDate && eventEndDate >= now) {
            upcoming.push(event);
          }
        });

        setUpcomingEvents(upcoming);
        setOnTheHorizonEvents(onTheHorizon);
      })
      .catch(console.error);
  }, [category]);

  if (upcomingEvents.length === 0 && onTheHorizonEvents.length === 0) {
    return null;
  }

  return (
    <Container>
      <h2 className="section-header green">Events</h2>

      {upcomingEvents.length > 0 && (
        <Section>
          <h2>Upcoming Events</h2>
          <EventGrid>
            {upcomingEvents.map((event) => (
              <EventCard key={event._id}>
                <LazyLoad height={200} offset={100}>
                  <EventImage
                    src={event.imageUrl || "/default-image.jpg"}
                    alt={event.title}
                  />
                </LazyLoad>
                <EventDetails>
                  <h3>{event.title}</h3>
                  <p>{event.description}</p>
                  {event.actionButtonLink && (
                    <ActionButton
                      href={event.actionButtonLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {event.actionButtonText || "Learn More"}
                    </ActionButton>
                  )}
                </EventDetails>
              </EventCard>
            ))}
          </EventGrid>
        </Section>
      )}

      {onTheHorizonEvents.length > 0 && (
        <Section>
          <h2>On the Horizon</h2>
          <EventGrid>
            {onTheHorizonEvents.map((event) => (
              <EventCard key={event._id}>
                <LazyLoad height={200} offset={100}>
                  <EventImage
                    src={event.imageUrl || "/default-image.jpg"}
                    alt={event.title}
                  />
                </LazyLoad>
                <EventDetails>
                  <h3>{event.title}</h3>
                  <p>{event.description}</p>
                  <ActionButton href="/event-interest">
                    Get Involved
                  </ActionButton>
                </EventDetails>
              </EventCard>
            ))}
          </EventGrid>
        </Section>
      )}

      <AllEventsLink href="/events">See All Events</AllEventsLink>
    </Container>
  );
};

export default DynamicEvents;

const Container = styled.div`
  margin: 40px auto;
  max-width: 1200px;
  padding: 0 20px;
`;

const MainTitle = styled.h1`
  text-align: center;
  font-size: 2.5rem;
  color: var(--hhb-primary);
  margin-bottom: 40px;
`;

const Section = styled.section`
  margin-bottom: 40px;

  h2 {
    color: var(--hhb-primary);
    margin-bottom: 20px;
  }
`;

const EventGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  grid-auto-rows: min-content; /* Adjusts rows to fit content */
  gap: 20px;
`;

const EventCard = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  background: var(--secondary-color);
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  padding: 20px;
  text-align: center;

  /* Ensure the card spans full width and height when necessary */
  &:nth-child(odd):last-child {
    grid-column: span 2; /* Spans full width if it's the only card in the row */
  }

  &:only-child {
    grid-column: span 1;
    grid-row: span 1; /* Ensures full-height adjustment */
  }
`;

const EventImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
  border-radius: 8px;
  margin-bottom: 15px;
`;

const EventDetails = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;

  h3 {
    margin-bottom: 10px;
    color: var(--primary-color);
    font-size: 1.4em;
    text-align: left;
  }

  p {
    margin-bottom: 20px;
    font-size: 0.9em;
    color: var(--text-color);
    text-align: left;
  }
`;

const ActionButton = styled.a`
  display: block;
  width: 100%; /* Full-width button */
  background-color: var(--hhb-primary);
  color: #fff;
  padding: 10px 0;
  border-radius: 5px;
  text-decoration: none;
  font-weight: bold;
  transition: background-color 0.3s;

  &:hover {
    background-color: var(--hhb-darkblue);
  }
`;

const AllEventsLink = styled.a`
  display: block;
  text-align: center;
  margin-top: 40px;
  font-size: 1.2rem;
  font-weight: bold;
  color: var(--hhb-primary);
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;
